/**
 * PermissionGuard - Componente de proteção de rota baseado em permissões
 * 
 * Verifica se o usuário tem permissão para acessar a página antes de renderizar.
 * Redireciona para página de acesso negado se não tiver permissão.
 * 
 * Regras de Acesso:
 * - root_admin e tenant_admin: acesso total a todas as rotas
 * - agent: acesso apenas ao 'chat' (hardcoded)
 * - tenant_supervisor: baseado no perfil de permissões associado
 * 
 * Segurança:
 * - Bloqueia acesso no nível de rota (não apenas oculta o menu)
 * - Registra tentativas de bypass na tabela security_bypass_attempts
 * - Usa usePermissions() para verificar permissões server-side
 * 
 * @example
 * // Rota que requer permissão do menu 'agents'
 * <PermissionGuard menuKey="agents">
 *   <TenantAgents />
 * </PermissionGuard>
 * 
 * @example
 * // Rota que requer ser admin (ignora perfil de permissões)
 * <PermissionGuard menuKey="settings" requireAdmin>
 *   <TenantSettings />
 * </PermissionGuard>
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions, BLOCKED_MENUS_FOR_SUPERVISOR } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { Loader2 } from 'lucide-react';

// Logger para auditoria de segurança
const securityLogger = createLogger('PermissionGuard');

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

interface PermissionGuardProps {
  /** Conteúdo a ser renderizado se tiver permissão */
  children: ReactNode;
  /** Key do menu para verificar permissão (ex: 'settings', 'agents', 'chat') */
  menuKey: string;
  /** Se true, apenas admin pode acessar (ignora perfil de permissões de supervisor) */
  requireAdmin?: boolean;
}

// =============================================================================
// FUNÇÃO PARA REGISTRAR TENTATIVA DE BYPASS
// =============================================================================

/**
 * Registra tentativa de acesso não autorizado na tabela security_bypass_attempts
 * Usado para auditoria e detecção de tentativas de invasão
 */
const logBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  menuKey: string,
  userRole: string | null,
  route: string
): Promise<void> => {
  try {
    // Só registra se tiver userId (usuário autenticado tentando bypass)
    if (!userId) return;

    const { error } = await supabase
      .from('security_bypass_attempts')
      .insert({
        user_id: userId,
        attempted_tenant_id: tenantId || null,
        function_name: 'PermissionGuard', // Campo obrigatório
        action: 'route_access_denied',
        attempted_value: route,
        user_agent: navigator.userAgent,
        details: {
          route,
          menu_key: menuKey,
          user_role: userRole,
          timestamp: new Date().toISOString(),
        },
      });

    if (error) {
      securityLogger.error('Falha ao registrar tentativa de bypass', { error: error.message });
    } else {
      securityLogger.warn('Tentativa de acesso não autorizado registrada', {
        route,
        menuKey,
        userRole,
      });
    }
  } catch (err) {
    securityLogger.error('Erro ao registrar bypass attempt', { err });
  }
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export function PermissionGuard({ 
  children, 
  menuKey, 
  requireAdmin = false 
}: PermissionGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();
  const { loading, userRole, isAdmin, canView } = usePermissions();
  
  // Ref para evitar múltiplos logs da mesma tentativa de bypass
  const hasLoggedBypass = useRef(false);

  // ==========================================================================
  // EFEITO PARA REGISTRAR TENTATIVA DE BYPASS
  // ==========================================================================
  
  useEffect(() => {
    // Só executa quando terminar de carregar e ainda não logou
    if (loading || hasLoggedBypass.current) return;

    // Verifica se é uma tentativa de acesso não autorizado
    const isUnauthorized = (() => {
      // Admin tem acesso total
      if (isAdmin) return false;

      // Rota requer admin mas usuário não é admin
      if (requireAdmin) return true;

      // Menu bloqueado para supervisores
      if (userRole === 'tenant_supervisor' && BLOCKED_MENUS_FOR_SUPERVISOR.includes(menuKey)) {
        return true;
      }

      // Verifica permissão do menu
      if (!canView(menuKey)) return true;

      return false;
    })();

    // Se não autorizado, registra tentativa
    if (isUnauthorized) {
      hasLoggedBypass.current = true;
      logBypassAttempt(
        user?.id,
        currentTenant?.id,
        menuKey,
        userRole,
        location.pathname
      );
    }
  }, [loading, isAdmin, requireAdmin, userRole, menuKey, canView, user?.id, currentTenant?.id, location.pathname]);

  // ==========================================================================
  // ESTADOS DE CARREGAMENTO
  // ==========================================================================

  // Exibe spinner enquanto verifica permissões
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ==========================================================================
  // VERIFICAÇÃO DE PERMISSÕES
  // ==========================================================================

  // Admin tem acesso total - renderiza diretamente
  if (isAdmin) {
    return <>{children}</>;
  }

  // Rota requer admin mas usuário não é admin
  if (requireAdmin) {
    securityLogger.warn('Acesso negado: rota requer admin', { 
      route: location.pathname, 
      userRole 
    });
    return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;
  }

  // Menu bloqueado para supervisores (hardcoded - company, settings)
  if (userRole === 'tenant_supervisor' && BLOCKED_MENUS_FOR_SUPERVISOR.includes(menuKey)) {
    securityLogger.warn('Acesso negado: menu bloqueado para supervisor', { 
      menuKey, 
      route: location.pathname 
    });
    return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;
  }

  // Verifica permissão do menu através do perfil de permissões
  if (!canView(menuKey)) {
    securityLogger.warn('Acesso negado: sem permissão para o menu', { 
      menuKey, 
      userRole, 
      route: location.pathname 
    });
    return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;
  }

  // ==========================================================================
  // USUÁRIO AUTORIZADO - RENDERIZA CONTEÚDO
  // ==========================================================================

  return <>{children}</>;
}
